/**
 * Form Service for dynamic routing and validation
 * Handles form slug validation, caching, and API integration
 */

import {
  API_BASE_PATH,
  SUBMISSIONS_API_BASE_PATH,
  API_ENDPOINTS,
  FORM_CONFIG,
  getApiBasePath,
  getSubmissionsApiBasePath
} from '@shared/constants'
import type { AxiosResponse } from 'axios'
import axios from 'axios'
import { ref, readonly } from 'vue'

// Types for form validation
export interface FormValidationResponse {
  isValid: boolean
  exists: boolean
  isActive: boolean
  formData: FormData | null
  error: FormError | null
}

export interface FormData {
  id: string | number
  slug: string
  title: string
  description: string
  isActive: boolean
  settings: Record<string, any>
  fields: any[]
  meeting_type?: any
  parametrized_fields?: Record<string, string>
  created_at?: string
  updated_at?: string
  redirect_url?: string | null
  thank_you_page_title?: string | null
  thank_you_page_message?: string | null
  email_verification_enabled?: boolean
  phone_verification_enabled?: boolean
}

export interface FormError {
  code: string
  message: string
  details: string
}

// Updated types for slot availability based on backend API v2 specification
export interface AvailableSlot {
  slot_start_datetime: string
  available_capacity?: number
  local_time: string
  local_date: string
}

// NEW: Availability Summary for calendar display
export interface AvailabilitySummary {
  has_slots: boolean
  total_capacity: number
  available_capacity: number
  slots_count: number
  peak_capacity_time: string | null
}

export interface SlotAvailabilityResponse {
  form: {
    slug: string
    name: string
    meeting_type: {
      name: string
      duration: number
    }
  }
  available_slots: AvailableSlot[]
  date_range: {
    start_date: string
    end_date: string
  }
  // NEW: Optional availability summary for calendar display
  availability_summary?: Record<string, AvailabilitySummary>
}

// NEW: Enhanced query params with summary support
export interface SlotQueryParams {
  form_slug: string
  start_date?: string
  end_date?: string
  timezone?: string
  include_summary?: boolean
}

interface CacheEntry {
  result: FormValidationResponse
  timestamp: number
}

export class FormService {
  private static cache = new Map<string, CacheEntry>()
  private static readonly CACHE_TTL = FORM_CONFIG.VALIDATION_CACHE_TTL

  /**
   * Get headers for API requests
   */
  public static getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    }
  }

  /**
   * Validate if a form slug exists and is accessible
   */
  static async validateFormSlug(slug: string): Promise<FormValidationResponse> {
    // Check cache first
    const cacheKey = `form_validation_${slug}`
    const cached = this.cache.get(cacheKey)

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.result
    }

    try {
      const response: AxiosResponse<any> = await axios.get(
        `${getApiBasePath()}${API_ENDPOINTS.FORM_BY_SLUG(slug)}`,
        {
          headers: this.getHeaders(),
          timeout: 30000
        }
      )

      const result: FormValidationResponse = {
        isValid: true,
        exists: true,
        isActive: response.data.is_active ?? true,
        formData: {
          id: response.data.id,
          slug: response.data.slug,
          title: response.data.title || response.data.name || 'Untitled Form',
          description: response.data.description || '',
          isActive: response.data.is_active ?? true,
          settings: response.data.settings || {},
          fields: response.data.fields || [],
          meeting_type: response.data.meeting_type || null,
          parametrized_fields: response.data.parametrized_fields || {},
          created_at: response.data.created_at || null,
          updated_at: response.data.updated_at || null,
          redirect_url: response.data.redirect_url || null,
          thank_you_page_title: response.data.thank_you_page_title || null,
          thank_you_page_message: response.data.thank_you_page_message || null,
          email_verification_enabled: response.data.email_verification_enabled ?? true,
          phone_verification_enabled: response.data.phone_verification_enabled ?? false
        },
        error: null
      }

      // Cache the result
      this.cache.set(cacheKey, {
        result,
        timestamp: Date.now(),
      })

      return result
    } catch (error) {
      console.error('Form validation error:', error)

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          const result: FormValidationResponse = {
            isValid: false,
            exists: false,
            isActive: false,
            formData: null,
            error: {
              code: 'FORM_NOT_FOUND',
              message: `Form with slug "${slug}" was not found.`,
              details: 'The form you are looking for does not exist or has been removed.'
            }
          }
          this.cache.set(cacheKey, { result, timestamp: Date.now() })
          return result
        }

        const result: FormValidationResponse = {
          isValid: false,
          exists: false,
          isActive: false,
          formData: null,
          error: {
            code: 'API_ERROR',
            message: 'An error occurred while validating the form.',
            details: error.message
          }
        }
        return result
      }

      throw error
    }
  }

  /**
   * Get available time slots for a form using v2 API
   */
  static async getAvailableSlots(
    formSlug: string,
    startDate?: string,
    endDate?: string,
    timezone: string = 'Europe/Warsaw'
  ): Promise<SlotAvailabilityResponse> {
    const params = new URLSearchParams({
      form_slug: formSlug,
      timezone
    })

    // Handle start_date parameter - for today, don't send start_date to let backend use current time
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    if (startDate && startDate !== todayStr) {
      // For future dates, send the date parameter
      params.append('start_date', startDate)
    }
    // For today or no startDate, let backend default to timezone.now() for proper time filtering

    if (endDate) {
      params.append('end_date', endDate)
    }

    try {
      const response: AxiosResponse<SlotAvailabilityResponse> = await axios.get(
        `${getApiBasePath()}${API_ENDPOINTS.AVAILABLE_SLOTS}?${params.toString()}`,
        {
          headers: this.getHeaders(),
          timeout: 30000
        }
      )

      return response.data
    } catch (error) {
      console.error('Error fetching available slots:', error)

      if (import.meta.env.DEV) {
        console.warn('🔧 [FormService] Development mode: Using simulated slot data')
        return this.simulateSlotAvailabilityResponse(formSlug, startDate, endDate)
      }

      throw error
    }
  }

  /**
   * Get available time slots with summary for calendar display using v2 API
   */
  static async getAvailableSlotsWithSummary(
    formSlug: string,
    startDate?: string,
    endDate?: string,
    timezone: string = 'Europe/Warsaw'
  ): Promise<SlotAvailabilityResponse> {
    const params = new URLSearchParams({
      form_slug: formSlug,
      timezone,
      include_summary: 'true'
    })

    // Handle start_date parameter - for today, don't send start_date to let backend use current time
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    if (startDate && startDate !== todayStr) {
      // For future dates, send the date parameter
      params.append('start_date', startDate)
    }
    // For today or no startDate, let backend default to timezone.now() for proper time filtering

    if (endDate) {
      params.append('end_date', endDate)
    }

    try {
      const response: AxiosResponse<SlotAvailabilityResponse> = await axios.get(
        `${getApiBasePath()}${API_ENDPOINTS.AVAILABLE_SLOTS}?${params.toString()}`,
        {
          headers: this.getHeaders(),
          timeout: 15000 // Longer timeout for summary requests
        }
      )

      return response.data
    } catch (error) {
      console.error('Error fetching available slots with summary:', error)

      if (import.meta.env.DEV) {
        console.warn('🔧 [FormService] Development mode: Using simulated slot data with summary')
        return this.simulateSlotAvailabilityResponseWithSummary(formSlug, startDate, endDate)
      }

      throw error
    }
  }

  /**
   * NEW: Get calendar availability summary for a date range (30-day optimized)
   */
  static async getCalendarAvailability(
    formSlug: string,
    startDate?: string,
    timezone: string = 'Europe/Warsaw'
  ): Promise<Record<string, AvailabilitySummary>> {
    const start = startDate ? new Date(startDate) : new Date()
    const end = new Date(start)
    end.setDate(start.getDate() + 30) // 30-day range for calendar

    const startStr = start.toISOString().split('T')[0]
    const endStr = end.toISOString().split('T')[0]

    const response = await this.getAvailableSlotsWithSummary(formSlug, startStr, endStr, timezone)
    return response.availability_summary || {}
  }

  /**
   * Simulate slot availability response for demo purposes
   */
  private static simulateSlotAvailabilityResponse(
    formSlug: string,
    startDate?: string,
    endDate?: string
  ): SlotAvailabilityResponse {
    const now = new Date()
    const start = startDate ? new Date(startDate) : new Date(now.getTime() + 24 * 60 * 60 * 1000) // Tomorrow
    const end = endDate ? new Date(endDate) : new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 days later

    const slots: AvailableSlot[] = []

    // Generate slots for each day in the range
    const currentDate = new Date(start)
    while (currentDate <= end) {
      // Skip weekends
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        // Generate slots from 9 AM to 5 PM
        for (let hour = 9; hour < 17; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const slotStart = new Date(currentDate)
            slotStart.setHours(hour, minute, 0, 0)

            // Only show available slots (70% chance)
            if (Math.random() > 0.3) {
              const localDate = slotStart.toISOString().split('T')[0]
              const localTime = slotStart.toTimeString().slice(0, 5)

              slots.push({
                slot_start_datetime: slotStart.toISOString(),
                available_capacity: Math.floor(Math.random() * 5) + 1,
                local_time: localTime,
                local_date: localDate
              })
            }
          }
        }
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return {
      form: {
        slug: formSlug,
        name: "Demo Form",
        meeting_type: {
          name: "Sales Call",
          duration: 30
        }
      },
      available_slots: slots,
      date_range: {
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0]
      }
    }
  }

  /**
   * NEW: Simulate slot availability response with summary for demo purposes
   */
  private static simulateSlotAvailabilityResponseWithSummary(
    formSlug: string,
    startDate?: string,
    endDate?: string
  ): SlotAvailabilityResponse {
    const baseResponse = this.simulateSlotAvailabilityResponse(formSlug, startDate, endDate)

    // Generate availability summary for each date
    const availabilitySummary: Record<string, AvailabilitySummary> = {}
    const start = new Date(baseResponse.date_range.start_date)
    const end = new Date(baseResponse.date_range.end_date)

    const currentDate = new Date(start)
    while (currentDate <= end) {
      const dateStr = currentDate.toISOString().split('T')[0]
      const daySlots = baseResponse.available_slots.filter(slot => slot.local_date === dateStr)

      if (daySlots.length > 0) {
        const totalCapacity = daySlots.reduce((sum, slot) => sum + (slot.available_capacity ?? 0), 0)
        const slotsCount = daySlots.length
        const peakSlot = daySlots.reduce((max, slot) =>
          (slot.available_capacity ?? 0) > (max.available_capacity ?? 0) ? slot : max
        )

        availabilitySummary[dateStr] = {
          has_slots: true,
          total_capacity: totalCapacity,
          available_capacity: totalCapacity,
          slots_count: slotsCount,
          peak_capacity_time: peakSlot.local_time
        }
      } else {
        availabilitySummary[dateStr] = {
          has_slots: false,
          total_capacity: 0,
          available_capacity: 0,
          slots_count: 0,
          peak_capacity_time: null
        }
      }

      currentDate.setDate(currentDate.getDate() + 1)
    }

    return {
      ...baseResponse,
      availability_summary: availabilitySummary
    }
  }

  /**
   * Submit a booking request
   */
  static async submitBooking(bookingData: BookingRequest): Promise<BookingResponse> {
    const url = `${getSubmissionsApiBasePath()}${API_ENDPOINTS.BOOKINGS}/`
    console.log('Submitting booking to:', url)
    try {
      const response: AxiosResponse<BookingResponse> = await axios.post(
        url,
        bookingData,
        {
          headers: this.getHeaders(),
          timeout: 60000,
        }
      )
      return response.data
    } catch (error) {
      console.error('Failed to submit booking:', error)
      throw error
    }
  }

  /**
   * Clear validation cache
   */
  static clearCache(slug?: string): void {
    if (slug) {
      this.cache.delete(`form_validation_${slug}`)
    } else {
      this.cache.clear()
    }
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheInfo(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    }
  }
}

/**
 * Composable for reactive form validation
 * Use this in components for reactive form state
 */
export function useFormValidation() {
  const isValidating = ref(false)
  const validationError = ref<string | null>(null)

  const validateSlug = async (slug: string): Promise<FormValidationResponse> => {
    isValidating.value = true
    validationError.value = null

    try {
      const result = await FormService.validateFormSlug(slug)
      if (!result.isValid && result.error) {
        validationError.value = result.error.message
      }
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Validation failed'
      validationError.value = errorMessage
      throw error
    } finally {
      isValidating.value = false
    }
  }

  return {
    isValidating: readonly(isValidating),
    validationError: readonly(validationError),
    validateSlug,
    clearValidationError: () => { validationError.value = null }
  }
}

export interface BookingRequest {
  form_id: number
  start_datetime: string
  form_data: Record<string, any>
}

export interface BookingResponse {
  id?: number
  form?: {
    id: number
    name: string
    slug: string
    meeting_type: {
      id: number
      name: string
      duration: number
    }
  }
  start_datetime?: string
  end_datetime?: string
  user?: any
  external_client_email?: string | null
  external_client_name?: string | null
  external_system_id?: string | null
  short_description?: string | null
  status: string
  status_display?: string
  cancellation_reason?: string | null
  rescheduled_from?: string | null
  submitted_at?: string
  updated_at?: string
  form_data?: Record<string, any>
  message?: string
  existing_booking?: {
    id: number | null
    start_datetime: string | null
  }
}

export interface BookingSubmissionResult {
  success: boolean
  booking_id?: number
  reference?: string
  status?: string
  message?: string
  booking?: BookingResponse
  start_datetime?: string
  end_datetime?: string
  client_email?: string | null
  client_name?: string | null
  form_name?: string
  meeting_type?: string
  calendar_links?: CalendarLinks
  error?: string | null
  field_errors?: Record<string, string[]>
}

export interface CalendarLinks {
  google: string
  outlook: string
  ics: string
}

export class CalendarService {
  /**
   * Generate calendar links for a booking
   */
  static generateCalendarLinks(booking: {
    start_datetime: string
    end_datetime: string
    meeting_type?: string
    form_name?: string
    client_name?: string
    booking_id?: number
  }): CalendarLinks {
    try {
      const startDate = new Date(booking.start_datetime)
      const endDate = new Date(booking.end_datetime)

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid dates provided to calendar service:', booking)
        throw new Error('Invalid date format provided')
      }

      const title = `Twoje spotkanie z Tutlo 💛`

      // Build description with joining instructions from email content
      const descriptionParts = [
        'Jak wziąć udział w spotkaniu? To proste!',
        '',
        '1. Zaloguj się na https://web.tutlo.com',
        '   Użyj tego samego adresu e-mail, na który otrzymałeś/aś to zaproszenie.',
        '   Jeśli nie masz hasła – spokojnie! Kliknij „Zapomniałeś hasło" i ustaw nowe.',
        '',
        '2. Szybki test połączenia – sprawdź, czy działa kamera i mikrofon, i jesteś gotowy/a!',
        '',
        '3. Gdy nadejdzie godzina spotkania, w Twoim kokpicie w kafelku z napisem',
        '   „UMÓWIONE SPOTKANIE" pojawi się przycisk "Połącz się".',
        '   Kliknij „Połącz się" – i już jesteś w rozmowie!',
        '',
        '4. Link do połączenia będzie aktywny 15 minut przed i 15 minut po zaplanowanej godzinie.',
        '   Nie musisz się stresować – masz czas!',
        '',
        'Do zobaczenia na platformie 💛',
        'Zespół Tutlo'
      ]


      // Add client name if available
      if (booking.client_name) {
        descriptionParts.push('', `--- Dane kontaktowe ---`, `Uczestnik: ${booking.client_name}`)
      }

      const description = descriptionParts.join('\n')

      const formatDate = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

      const startFormatted = formatDate(startDate)
      const endFormatted = formatDate(endDate)

      return {
        google: this.generateGoogleCalendarLink(title, description, startFormatted, endFormatted),
        outlook: this.generateOutlookLink(title, description, startFormatted, endFormatted),
        ics: this.generateICSContent(title, description, startDate, endDate, booking.booking_id || 0)
      }
    } catch (error) {
      console.error('Error generating calendar links:', error)
      // Return fallback links
      return {
        google: '#',
        outlook: '#',
        ics: '#'
      }
    }
  }

  private static generateGoogleCalendarLink(title: string, description: string, start: string, end: string): string {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${start}/${end}`,
      details: description
    })
    return `https://calendar.google.com/calendar/render?${params.toString()}`
  }

  private static generateOutlookLink(title: string, description: string, start: string, end: string): string {
    const params = new URLSearchParams({
      subject: title,
      startdt: start,
      enddt: end,
      body: description
    })
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
  }

  private static generateICSContent(title: string, description: string, startDate: Date, endDate: Date, bookingId: number): string {
    // Generate ICS content as data URL for immediate download
    const formatDateICS = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

    // Escape special characters for ICS format
    const escapeICSText = (text: string) => text.replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Tutlo Scheduler//Meeting Booking//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:booking-${bookingId}-${Date.now()}@tutlo.com`,
      `DTSTAMP:${formatDateICS(new Date())}`,
      `DTSTART:${formatDateICS(startDate)}`,
      `DTEND:${formatDateICS(endDate)}`,
      `SUMMARY:${escapeICSText(title)}`,
      `DESCRIPTION:${escapeICSText(description)}`,
      'STATUS:CONFIRMED',
      'TRANSP:OPAQUE',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n')

    return `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`
  }
}
